import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  Box,
  Divider,
  Flex,
  Icon,
  Image,
  Text,
  VStack,
  Link as LinkTag,
  HStack,
} from '@chakra-ui/react';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/image/4BoxesHeader.png';
import Book from './icons/Book';
import Boxes from './icons/Boxes';
import Calendar from './icons/Calendar';
import Documents from './icons/Documents';
import Help from './icons/Help';
import Home from './icons/Home';
import Key from './icons/Key';
import Logout from './icons/Logout';
import Settings from './icons/Settings';
import NavItem from './NavItem';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { getCurrentUserInfo } from '../utils/getCurrentUserInfo';
import { unAssume } from '../stores/slices/user/userSlice';
import { BiBookReader } from 'react-icons/bi';

const Sidebar = props => {
  const { userInfo, isAssumed, assumedUserInfo } = useSelector(
    state => state.user
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getCurrentUserInfo({
    assumedUserInfo,
    isAssumed,
    userInfo,
  });
  return (
    <Box
      as="nav"
      pos="fixed"
      top="0"
      left="0"
      zIndex="sticky"
      h="full"
      overflowX="hidden"
      overflowY="auto"
      bg="white"
      w="64"
      {...props}
    >
      <Scrollbars>
        <VStack justifyContent={'space-between'} h="full">
          <VStack spacing={0}>
            <Box px="2" py="8" align="center">
              <Image src={logo} alt="4 boxes logo" objectFit="contain" />
            </Box>
            <Flex
              direction="column"
              fontSize="sm"
              aria-label="Main Navigation"
              px={4}
              w={'full'}
            >
              <Text px={7} color={'lightGray.500'} mb="5">
                MAIN MENU
              </Text>
              <NavItem icon={Home} to="/dashboard">
                Dashboard
              </NavItem>
              <NavItem icon={Boxes} to="my-4boxes">
                My 4Boxes
              </NavItem>
              {!isAssumed && (
                <NavItem icon={Book} to="journal">
                  Journal
                </NavItem>
              )}
              <NavItem icon={Documents} to="resources">
                Resources
              </NavItem>
              {!isAssumed && (
                <NavItem icon={Key} to="vault">
                  Vault
                </NavItem>
              )}
              {!isAssumed && (
                <NavItem icon={Calendar} to="reminders">
                  Reminders
                </NavItem>
              )}
            </Flex>
            <Divider />
            <Flex direction="column" fontSize="sm" px={4} w={'full'} pt={4}>
              <Text px={7} color={'lightGray.500'} mb="5">
                SUPPORT
              </Text>
              <Accordion
                allowToggle
                defaultIndex={
                  location.pathname.split('/').includes('settings') ? 0 : null
                }
              >
                <AccordionItem border={'0'}>
                  {({ isExpanded }) => (
                    <React.Fragment>
                      <LinkTag
                        as={AccordionButton}
                        align="center"
                        px="7"
                        mb={2}
                        borderRadius="1rem"
                        py="4"
                        justifyContent={'space-between'}
                        role="group"
                        cursor="pointer"
                        fontSize={'14px'}
                        fontWeight="semibold"
                        transition=".15s ease"
                        _hover={{
                          bg: 'primaryOpaque',
                          color: 'primary',
                        }}
                        _expanded={{ bg: 'primaryOpaque', color: 'primary' }}
                      >
                        <HStack spacing={0}>
                          <Icon
                            mr="2"
                            boxSize="5"
                            color={isExpanded ? 'primary' : 'lightGray.500'}
                            _groupHover={{
                              color: 'primary',
                            }}
                            as={Settings}
                          />
                          <Text fontSize={'14px'} fontWeight="semibold">
                            Settings
                          </Text>
                        </HStack>
                        <AccordionIcon />
                      </LinkTag>
                      <AccordionPanel
                        backgroundColor={'primaryOpaque'}
                        borderRadius={'1rem'}
                        pb={0}
                      >
                        <Flex direction="column" fontSize="sm" w={'full'}>
                          {!isAssumed && (
                            <Text color={'lightGray.500'} mb="5">
                              Templates
                            </Text>
                          )}
                          {!isAssumed && (
                            <NavItem to="settings/templates/journal">
                              Journal
                            </NavItem>
                          )}
                          {(currentUser.role === 'coach' ||
                            currentUser.role === 'admin') && (
                            <NavItem to="settings/templates/4boxes">
                              4Boxes
                            </NavItem>
                          )}
                          {!isAssumed && <Divider mb={'4'} />}
                          {currentUser.role !== 'employee' && (
                            <Text color={'lightGray.500'} mb="5">
                              Organisations
                            </Text>
                          )}

                          {currentUser.role === 'admin' && (
                            <NavItem to="settings/organisations">
                              Organisations and Users
                            </NavItem>
                          )}
                          {currentUser.role === 'admin' && (
                            <NavItem to="settings/organisation/coaches">
                              Coaches
                            </NavItem>
                          )}
                          {(currentUser.role === 'employer' ||
                            currentUser.role === 'admin') && (
                            <NavItem to="settings/organisation">
                              My Organisation
                            </NavItem>
                          )}
                          {(currentUser.role === 'employer' ||
                            currentUser.role === 'manager') && (
                            <NavItem to="settings/organisation/employees">
                              Employees
                            </NavItem>
                          )}
                          {currentUser.role === 'coach' && (
                            <NavItem to="settings/organisation/user-access">
                              My Users
                            </NavItem>
                          )}
                          {isAssumed && (
                            <NavItem
                              to="/#"
                              onClick={e => {
                                e.preventDefault();
                                dispatch(unAssume());
                                navigate('/dashboard');
                              }}
                            >
                              Return to My Account
                            </NavItem>
                          )}
                        </Flex>
                      </AccordionPanel>
                    </React.Fragment>
                  )}
                </AccordionItem>
              </Accordion>
              <NavItem
                icon={BiBookReader}
                to="https://learn.4boxes.com.au/"
                isExternal={true}
              >
                Learn
              </NavItem>
              <NavItem icon={Help} to="help">
                Help
              </NavItem>
              <NavItem icon={Logout} to="/user/auth">
                Sign Out
              </NavItem>
            </Flex>
          </VStack>
          <Box px={4} w={'full'}>
            <Link to="profile">
              <Flex
                align="center"
                p="2"
                mb={4}
                borderRadius="1rem"
                cursor="pointer"
                role="group"
                fontWeight="semibold"
                transition=".15s ease"
                bg={'gray.500'}
              >
                <Avatar
                  mr="2"
                  size={['md', 'lg']}
                  name={currentUser.full_name}
                  src={currentUser.image?.url}
                  cursor="pointer"
                  showBorder
                  borderColor={'primary'}
                />
                <VStack alignItems={'start'} spacing={'0'}>
                  <Text color={'primary'} fontWeight={'bold'} fontSize={'md'}>
                    {currentUser.full_name} {isAssumed ? '(Assumed)' : ''}
                  </Text>
                  <Text
                    color={'lightGray.500'}
                    fontSize={'md'}
                    fontWeight={'400'}
                    textTransform={'capitalize'}
                  >
                    {currentUser.role}
                  </Text>
                </VStack>
              </Flex>
            </Link>
          </Box>
        </VStack>
      </Scrollbars>
    </Box>
  );
};
export default Sidebar;
